// Skill steps in Activity, in the reader's words instead of raw tool data:
// find_right_skill says what Poneva looked for and which Skills it found (a
// row opens the Skill); read_company_skill names the Skill and version read.
import type { ReactNode } from 'react';
import type { ActivityTraceStep } from '@/components/assistant-ui/activity-trace';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import skillsCopy from '@/i18n/ns/skills';
import { useWorkspace } from './context';
import { WorkspaceIcon } from './icons';
import './skill-activity.css';

type Json = Record<string, unknown>;
const record = (value: unknown): Json | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Json) : null;
const text = (value: unknown) => (typeof value === 'string' ? value.trim() : '');

function args(step: ActivityTraceStep): Json | null {
  try {
    return record(step.argsText ? JSON.parse(step.argsText) : null);
  } catch {
    return null;
  }
}

interface FoundSkill {
  id: string;
  name: string;
  useWhen: string;
}
const foundSkills = (result: Json | null): FoundSkill[] =>
  (Array.isArray(result?.skills) ? result.skills : [])
    .map(record)
    .filter((skill): skill is Json => Boolean(skill && text(skill.id) && text(skill.name)))
    .map((skill) => ({ id: text(skill.id), name: text(skill.name), useWhen: text(skill.useWhen) }));

function FoundSkills({ step }: { step: ActivityTraceStep }) {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(skillsCopy);
  const result = record(step.result);
  const need = text(args(step)?.need) || text(result?.need);
  const skills = foundSkills(result);
  return (
    <div className="ws-skill-step">
      <dl className="pc-tool-data">
        {need && (
          <div>
            <dt>{copy.lookedFor}</dt>
            <dd>{need}</dd>
          </div>
        )}
        <div>
          <dt>{copy.searched}</dt>
          <dd>{copy.searchedSkills}</dd>
        </div>
      </dl>
      {skills.length ? (
        <>
          <h3>{copy.found}</h3>
          <div className="ws-skill-step-found">
            {skills.map((skill, index) => (
              <button
                type="button"
                key={skill.id}
                className="ws-skill-step-row"
                data-canvas-trigger="true"
                onClick={() => ws.openCanvas({ kind: 'skill', id: skill.id })}
              >
                <WorkspaceIcon name="skills" size={15} />
                <span>
                  <b>{skill.name}</b>
                  {skill.useWhen && <small>{skill.useWhen}</small>}
                </span>
                {index === 0 && <em>{copy.bestFit}</em>}
                <WorkspaceIcon name="chevron" size={14} />
              </button>
            ))}
          </div>
        </>
      ) : (
        <p>{copy.noneFound}</p>
      )}
    </div>
  );
}

function SkillRead({ step }: { step: ActivityTraceStep }) {
  const copy = useNamespaceCopy(skillsCopy);
  const result = record(step.result);
  const skill = record(result?.skill);
  const version = record(skill?.published) ?? record(skill?.draft) ?? skill;
  const name = text(skill?.name) || text(version?.name);
  const revision = Number.isInteger(skill?.revision) ? skill?.revision : version?.revision;
  if (!name) return null;
  return (
    <div className="ws-skill-step">
      <dl className="pc-tool-data">
        <div>
          <dt>{copy.skillLabel}</dt>
          <dd>
            {name}
            {Number.isInteger(revision)
              ? ` · ${formatCopy(copy.cardVersion, { revision: revision as number })}`
              : ''}
          </dd>
        </div>
      </dl>
    </div>
  );
}

/** The Skill tools' own step evidence; null keeps the generic Input and Output. */
export function skillStepDetail(step: ActivityTraceStep): ReactNode | null {
  if (step.outcome === 'error' || step.result === undefined) return null;
  if (step.toolName === 'find_right_skill') return <FoundSkills step={step} />;
  if (step.toolName === 'read_company_skill') return <SkillRead step={step} />;
  return null;
}
